/** A single run: the version's graph coloured by step status, plus each step's attempts, output, errors and log. */
import { Fragment, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Group, Panel, Separator, useDefaultLayout } from 'react-resizable-panels';
import { ArrowLeft, Ban, Loader2, RotateCcw } from 'lucide-react';
import { NODE_TYPES, TRIGGER_LABELS, type NodeRunStatus } from '@prism/contracts';
import { api, errorMessage } from '../../api/client';
import { useMe, useRun } from '../../api/hooks';
import { toast } from '../../components/Toasts';
import { dateTime, elapsed, relative } from '../../lib/format';
import { encoderText } from '../../lib/encoder';
import { GraphCanvas, nodeIcon } from './GraphCanvas';
import { RunStatus } from './Workflows';
import { queryKeys } from '../../api/queryKeys';
import { layoutStorage } from '../../lib/prefs';

const NODE_LABEL: Record<NodeRunStatus, string> = {
  pending: 'Not reached', running: 'Running', retrying: 'Retrying', waiting_approval: 'Waiting for approval', succeeded: 'Succeeded', failed: 'Failed', skipped: 'Skipped', cancelled: 'Cancelled',
};

/** 1.9.3: a pane of the resizable group: its content (the canvas, the details) scrolls itself. */
const PANE = { display: 'flex', overflow: 'hidden' } as const;

export function RunPage() {
  const { id } = useParams();
  const q = useRun(id);
  const me = useMe().data!;
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [selected, setSelected] = useState<string | null>(null);
  // 1.9.3: the details pane is resizable, as the flow editor's inspector (remembered per browser).
  const layout = useDefaultLayout({ id: 'prism-flow-run', storage: layoutStorage });
  if (q.isError) return <div className="empty"><div className="empty-inner"><h2>Run not found</h2><Link className="btn" to="/workflows/runs">All runs</Link></div></div>;
  const r = q.data;
  if (!r) return <div className="empty"><Loader2 className="spin" color="var(--accent)" /></div>;
  const canManage = me.permissions.includes('automation.manage');
  const finished = ['succeeded', 'failed', 'cancelled'].includes(r.status);
  const statuses = new Map(r.nodes.map((n) => [n.nodeId, n.status]));
  const ordered = r.nodes.filter((n) => n.status !== 'pending').sort((a, b) => (a.startedAt ?? '9').localeCompare(b.startedAt ?? '9'));
  const shown = selected ? r.nodes.filter((n) => n.nodeId === selected) : ordered;

  async function act(kind: 'cancel' | 'rerun') {
    try {
      const out = await api<{ runId?: string }>(`/api/v1/automation-runs/${r!.id}/${kind}`, { method: 'POST' });
      if (kind === 'rerun' && out.runId) navigate(`/workflows/runs/${out.runId}`);
      else { toast('Cancelling the run', 'info'); await qc.invalidateQueries({ queryKey: queryKeys.automationRun(r!.id) }); }
    } catch (err) {
      toast(errorMessage(err), 'error');
    }
  }

  return (
    <div className="builder">
      <header className="topbar">
        <Link to="/workflows/runs" className="btn ghost icon" aria-label="Back to runs"><ArrowLeft size={16} /></Link>
        <h1><Link to={`/workflows/edit/${r.definitionId}`}>{r.definitionName}</Link> <span className="crumb">· v{r.version} · run {r.id.slice(0, 8)}</span></h1>
        <RunStatus status={r.status} />
        <div className="topbar-spacer" />
        {canManage && !finished && <button className="btn" onClick={() => void act('cancel')} data-testid="cancel-run"><Ban size={14} /> Cancel</button>}
        {canManage && finished && <button className="btn" onClick={() => void act('rerun')} data-testid="rerun"><RotateCcw size={14} /> Run again</button>}
      </header>
      <div className="builder-body run-body">
        <Group orientation="horizontal" className="panel-group builder-panels" defaultLayout={layout.defaultLayout} onLayoutChanged={layout.onLayoutChanged}>
        <Panel id="flow-run-canvas" minSize="320px" className="builder-pane" style={PANE}>
        {/* Read-only: one step at most (the canvas takes a list since 1.9.3). */}
        <GraphCanvas graph={r.graph} selected={selected ? [selected] : []} onSelect={(ids) => setSelected(ids[ids.length - 1] ?? null)} statuses={statuses} readOnly />
        </Panel>
        <Separator className="resize-handle" id="run-inspector-resize" />
        <Panel id="flow-run-inspector" defaultSize="400px" minSize="300px" maxSize="60%" className="builder-pane" style={PANE}>
        <aside className="builder-inspector">
          <div className="inspector-pane">
            <dl className="kv">
              <dt>Started by</dt><dd>{TRIGGER_LABELS[r.trigger.type]}{r.trigger.actor ? ` · ${r.trigger.actor}` : ''}</dd>
              {r.trigger.assetId && <><dt>Asset</dt><dd><Link to={`/assets/${r.trigger.assetId}`}>{r.trigger.assetTitle ?? r.trigger.assetId}</Link></dd></>}
              {r.trigger.date && (
                // Date in metadata (1.0.0-rc.10): which date came due, when, and whether the run caught up after downtime.
                <>
                  <dt>Date</dt><dd className="mono" data-testid="run-trigger-date">{r.trigger.date.fieldId} = {r.trigger.date.date}</dd>
                  <dt>Due at</dt><dd title={r.trigger.date.firingAt}>{dateTime(r.trigger.date.firingAt)}{r.trigger.date.caughtUp ? ' · caught up after downtime' : ''}{r.trigger.date.pastDate ? ' · a date already passed' : ''}</dd>
                </>
              )}
              {r.trigger.sequenceId && <><dt>Sequence</dt><dd><Link to={`/create/${r.trigger.sequenceId}`}>{r.trigger.sequenceName ?? r.trigger.sequenceId}</Link></dd></>}
              <dt>Created</dt><dd title={dateTime(r.createdAt)}>{relative(r.createdAt)}</dd>
              <dt>Duration</dt><dd className="mono">{r.startedAt ? elapsed(r.startedAt, r.finishedAt) : '—'}</dd>
              <dt>Workflow id</dt><dd className="mono faint" style={{ fontSize: 11 }}>{r.workflowId}</dd>
            </dl>
            {r.error && <div className="banner error" data-testid="run-error"><b>{r.error.code}</b> {r.error.message}</div>}
            {r.status === 'waiting_resources' && <div className="banner warning">This workflow is at its concurrency limit; the run starts when a slot frees up.</div>}
            {r.approvals.filter((a) => a.status === 'pending').map((a) => (
              <div key={a.id} className="banner warning">Waiting for {a.approverUserId ? a.approverName ?? 'the chosen person' : `a ${(a.role ?? '').replace('_', ' ')}`} to decide. <Link to={`/tasks?view=approvals&scope=all&approval=${a.id}`}>Open in Tasks</Link></div>
            ))}
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <p className="section-label" style={{ flex: 1 }}>{selected ? 'Step' : 'Steps'}</p>
              {selected && <button className="btn ghost sm" onClick={() => setSelected(null)}>Show all</button>}
            </div>
            {shown.length === 0 && <span className="muted">{selected ? 'This step has not run.' : 'No step has started yet.'}</span>}
            <div className="steps" data-testid="run-steps">
              {shown.map((n) => {
                const def = r.graph.nodes.find((x) => x.id === n.nodeId);
                const approval = r.approvals.find((a) => a.nodeId === n.nodeId && a.status !== 'pending');
                return (
                  <div className="step" key={n.nodeId} data-node-status={n.status} data-node={n.nodeId}>
                    <span className={`step-icon ${n.status}`}>{def ? nodeIcon(def.type) : null}</span>
                    <div className="step-main">
                      <span className="step-title">{def?.label || (def ? NODE_TYPES[def.type].label : n.nodeId)}</span>
                      <span className="step-sub">{NODE_LABEL[n.status]}{n.attempts > 1 ? ` · attempt ${n.attempts}` : ''}</span>
                      {n.error && <span className="warn-text" style={{ fontSize: 12 }}>{n.error.message}{n.error.retryable ? '' : ' (not retryable)'}</span>}
                      {approval && <span className="step-sub">{approval.status} by {approval.decidedBy}{approval.comment ? `: “${approval.comment}”` : ''}</span>}
                      {n.output && Object.keys(n.output).length > 0 && (
                        <dl className="kv compact">{Object.entries(n.output).filter(([k]) => k !== 'edl').map(([k, v]) => <Fragment key={k}><dt>{k}</dt><dd className="mono">{typeof v === 'object' ? JSON.stringify(v) : String(v)}</dd></Fragment>)}</dl>
                      )}
                      {encoderText(n.output?.encoder, n.output?.decoder) && <span className="step-sub" data-testid="node-encoder">{encoderText(n.output?.encoder, n.output?.decoder)}</span>}
                      {def?.type === 'run_qc' && typeof n.output?.runId === 'string' && r.trigger.assetId && (
                        <Link className="btn sm" style={{ alignSelf: 'flex-start' }} data-testid="run-qc-link" to={`/assets/${r.trigger.assetId}?tab=qc&run=${n.output.runId}`}>
                          QC {String(n.output.status)} · open the QC tab
                        </Link>
                      )}
                      {def?.type === 'create_task' && typeof n.output?.taskId === 'string' && (
                        <Link className="btn sm" style={{ alignSelf: 'flex-start' }} data-testid="run-task-link" to={`/tasks?task=${n.output.taskId}&scope=all`}>Open the task</Link>
                      )}
                      {typeof n.output?.edl === 'string' && (
                        <button className="btn sm" style={{ alignSelf: 'flex-start' }} data-testid="download-run-edl" onClick={() => {
                          const url = URL.createObjectURL(new Blob([n.output!.edl as string], { type: 'text/plain' }));
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = `${r.trigger.sequenceName ?? 'sequence'}.edl`;
                          a.click();
                          URL.revokeObjectURL(url);
                        }}>Download EDL</button>
                      )}
                      {n.logs.length > 0 && (
                        <ul className="log-list">{n.logs.map((l, i) => <li key={i} className={`log-${l.level}`}><span className="faint mono">{new Date(l.at).toLocaleTimeString()}</span> {l.message}</li>)}</ul>
                      )}
                    </div>
                    <span className="step-time">{n.startedAt ? elapsed(n.startedAt, n.finishedAt) : ''}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </aside>
        </Panel>
        </Group>
      </div>
    </div>
  );
}
